
import React from 'react';
import { TrendingUp } from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, ReferenceLine } from 'recharts';
import { formatTime } from '../utils/chartUtils';

interface WeeklyChartProps {
  dailyProgress: any[];
  isAheadOfTarget: boolean;
  roundedMax: number;
  yAxisTicks: number[];
}

const WeeklyChart: React.FC<WeeklyChartProps> = ({
  dailyProgress,
  isAheadOfTarget,
  roundedMax,
  yAxisTicks
}) => {
  const chartConfig = {
    target: {
      label: "Target",
      color: "hsl(var(--muted-foreground))",
    },
    progress: {
      label: "Progress",
      color: "hsl(var(--primary))",
    },
  };

  // Generate reference lines for grid
  const generateGridLines = () => {
    const lines = [];
    
    // Full hour lines (solid, light gray)
    for (let i = 60; i <= roundedMax; i += 60) {
      lines.push(
        <ReferenceLine 
          key={`hour-${i}`} 
          y={i} 
          stroke="hsl(var(--border))" 
          strokeWidth={1}
          strokeOpacity={0.3}
        />
      );
    }
    
    // Half hour lines (dotted, light gray)
    for (let i = 30; i <= roundedMax; i += 60) {
      if (i !== roundedMax) { // Don't add dotted line at the top
        lines.push(
          <ReferenceLine 
            key={`half-hour-${i}`} 
            y={i} 
            stroke="hsl(var(--border))" 
            strokeWidth={1}
            strokeDasharray="2 2"
            strokeOpacity={0.2}
          />
        );
      }
    }
    
    return lines;
  };

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isAheadOfTarget && (
            <div className="flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className="text-xs font-medium text-green-600 dark:text-green-400">
                Above target
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5 bg-primary rounded"></div>
            <span className="text-gray-600 dark:text-gray-400">Progress</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-px border-t-2 border-dashed border-muted-foreground"></div>
            <span className="text-gray-600 dark:text-gray-400">Target</span>
          </div>
        </div>
      </div>
      
      <div className="h-80 w-full">
        <ChartContainer config={chartConfig} className="h-full w-full">
          <LineChart data={dailyProgress} margin={{ top: 20, right: 20, bottom: 40, left: 10 }}>
            {generateGridLines()}
            <XAxis 
              dataKey="day" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              height={60}
            />
            <YAxis 
              domain={[0, roundedMax]}
              ticks={yAxisTicks}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value) => formatTime(value)}
              width={35}
            />
            <ChartTooltip 
              content={<ChartTooltipContent 
                formatter={(value, name) => [
                  formatTime(Number(value)), 
                  name === 'minutes' ? 'Progress' : 'Target'
                ]}
              />} 
            />
            <Line
              type="monotone"
              dataKey="target"
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="minutes"
              stroke="hsl(var(--primary))"
              strokeWidth={3}
              dot={{
                fill: 'hsl(var(--primary))',
                strokeWidth: 2,
                stroke: 'hsl(var(--background))',
                r: 4
              }}
            />
          </LineChart>
        </ChartContainer>
      </div>
    </div>
  );
};

export default WeeklyChart;
